import React from 'react';
import { Field } from 'redux-form';
import { months } from '../../../../Utils/months';
import { TYPE_OF_ROW, TYPE_OF_FLOW } from '../../../../../../utility/constants';

// Componente para botones de agregar ingreso/salida
export const AddTransactionButtons = ({ fields, setCollapsed, transactionType = "FINANCIAL" }) => {
    const handleButtonHover = (e, color) => {
        e.target.style.backgroundColor = color;
        e.target.style.color = 'white';
    };

    const handleButtonLeave = (e, color) => {
        e.target.style.backgroundColor = 'transparent';
        e.target.style.color = color;
    };

    const buttonStyle = (color) => ({
        color: color,
        fontWeight: 'bold',
        borderColor: color,
        padding: '8px 16px',
        fontSize: '14px'
    });

    return (
        <div className="mt-2">
            <div className="d-flex justify-content-between">
                <div 
                    type="button" 
                    className="btn btn-outline-success flex-fill mr-2 d-flex justify-content-center align-items-center agregar-tab-generica"
                    style={buttonStyle('#28a745')}
                    onMouseEnter={(e) => handleButtonHover(e, '#28a745')}
                    onMouseLeave={(e) => handleButtonLeave(e, '#28a745')}
                    onClick={() => {
                        fields.push({
                            isNew: true,
                            is_manual: true,
                            is_dollar: true,
                            type_of_flow: TYPE_OF_FLOW.INGRESOS,
                            type_of_row: TYPE_OF_ROW[transactionType]
                        });
                        setCollapsed(false);
                    }}>
                    + AGREGAR INGRESO
                </div>
                <div 
                    type="button" 
                    className="btn btn-outline-danger flex-fill ml-2 d-flex justify-content-center align-items-center agregar-tab-generica"
                    style={buttonStyle('#dc3545')}
                    onMouseEnter={(e) => handleButtonHover(e, '#dc3545')}
                    onMouseLeave={(e) => handleButtonLeave(e, '#dc3545')}
                    onClick={() => {
                        fields.push({
                            isNew: true,
                            is_manual: true,
                            is_dollar: true,
                            type_of_flow: TYPE_OF_FLOW.SALIDAS,
                            type_of_row: TYPE_OF_ROW[transactionType]
                        });
                        setCollapsed(false);
                    }}>
                    + AGREGAR SALIDA
                </div>
            </div>
        </div>
    );
};

// Función para separar campos por tipo
export const separateFieldsByType = (fields) => {
    const allFields = fields.getAll() || [];
    const ingresos = [];
    const salidas = [];

    allFields.forEach((field, index) => {
        if (field.isTitulo) return; // Para operating flow
        
        if (field.type_of_flow === TYPE_OF_FLOW.SALIDAS) {
            salidas.push({ field, index });
        } else {
            ingresos.push({ field, index });
        }
    });

    return { ingresos, salidas };
};

// Función para calcular totales por sección
export const calculateSectionTotals = (sectionFields) => {
    const totals = {total: 0};
    months.forEach(month => totals[month] = 0);
    
    sectionFields.forEach(({ field }) => {
        if (!field.isTitulo) { // Excluir títulos del cálculo
            months.forEach(month => {
                const value = parseFloat(field[month]) || 0;
                totals[month] += value;
            });
            totals.total += parseFloat(field.total) || 0;
        }
    });
    
    return totals;
};

// Función para renderizar headers de sección
export const renderSectionHeader = (title, bgColor = "#E8F4FD", sectionFields = [], currency) => {
    const sectionTotals = calculateSectionTotals(sectionFields);
    const isSalidaSection = title === 'SALIDAS';
    const titleStyle = isSalidaSection ? 
        { fontWeight: 'bold', fontSize: '14px', padding: '8px 12px', color: '#dc3545 !important' } : 
        { fontWeight: 'bold', fontSize: '14px', padding: '8px 12px' };
    
    return (
        <tr style={{ backgroundColor: bgColor }}>
            <td className='first-column table-form-fixed-cell-left-0 titulo-left-padded' 
                style={{ 
                    ...titleStyle,
                    borderLeft: `4px solid ${title === 'INGRESOS' ? '#28a745' : '#dc3545'}`
                }}>
                <span style={isSalidaSection ? { color: '#dc3545', fontWeight: 'bold' } : {}}>
                    {title}
                </span>
            </td>
            <td className='table-form-fixed-cell-left-1 text-right-padded' style={{ fontWeight: 'bold' }}>
                {formatCurrency(sectionTotals.total, currency, isSalidaSection)}
            </td>
            {months.map((month, idx) => (
                <td key={idx} className='text-right-padded' style={{ fontWeight: 'bold' }}>
                    {formatCurrency(sectionTotals[month], currency, isSalidaSection)}
                </td>
            ))}
            <td></td>
        </tr>
    );
};

// Componente para mostrar valores con formato consistente
export const CurrencyDisplay = ({ value, currency, isSalida }) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '';
    }
    
    const numericValue = parseFloat(value) || 0;
    const absValue = Math.abs(numericValue);
    const formatted = currency === 'USD'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(absValue)
        : new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(absValue);
    
    const shouldShowParentheses = isSalida || numericValue < 0;
    const displayValue = shouldShowParentheses ? `(${formatted})` : formatted;
    
    return isSalida ? 
        <span style={{ color: '#dc3545', fontWeight: '500' }}>{displayValue}</span> : 
        displayValue;
};

// Función para formatear números negativos entre paréntesis
export const formatCurrency = (value, currency, isNegative = false) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '';
    }
    
    const numericValue = parseFloat(value) || 0;
    const absValue = Math.abs(numericValue);
    const formatted = currency === 'USD'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(absValue)
        : new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(absValue);
    
    const isActuallyNegative = isNegative || numericValue < 0;
    const displayValue = isActuallyNegative ? `(${formatted})` : formatted;
    
    return isActuallyNegative ? 
        <span style={{ color: '#dc3545', fontWeight: '500' }}>{displayValue}</span> : 
        displayValue;
};

// Función optimizada para actualizar resumen - con mejor timing y performance
export const updateSummaryTotalsOptimized = (change, formValues) => {
    // Usar setTimeout para asegurar que Redux Form haya actualizado el estado
    setTimeout(() => {
        const { investments, operation, financial, summary } = formValues;
        
        if (!summary || summary.length < 5) {
            return;
        }
        
        const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                       'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

        // Índices del resumen
        const SALDO_INICIAL_INDEX = 0;
        const ENTRADAS_INDEX = 1;
        const SALIDAS_INDEX = 2;
        const FLUJO_NETO_INDEX = 3;
        const SALDO_FINAL_INDEX = 4;

        // Calcular todos los meses de una vez para mejor performance
        const updates = {};
        
        months.forEach(month => {
            let totalEntradas = 0;
            let totalSalidas = 0;

            // Procesar flujos financieros y operativos
            const flujosNormales = [
                ...(financial || []),
                ...(operation || []).filter(item => !item.isTitulo),
            ];

            flujosNormales.forEach(item => {
                if (item.isTitulo || item.is_not_suma) {
                    return;
                }

                const valor = parseFloat(item[month]) || 0;
                
                if (valor < 0) {
                    totalSalidas += valor;
                } else {
                    totalEntradas += valor;
                }
            });

            // Procesar inversiones (siempre negativas)
            const inversionesData = investments && investments.results ? investments.results : [];
            inversionesData.forEach(item => {
                if (item.isTitulo || item.is_not_suma) {
                    return;
                }

                const valor = parseFloat(item[month]) || 0;
                const valorInversion = valor > 0 ? -valor : valor;
                totalSalidas += valorInversion;
            });

            // Preparar actualizaciones en batch
            updates[`summary.${ENTRADAS_INDEX}.${month}`] = totalEntradas;
            updates[`summary.${SALIDAS_INDEX}.${month}`] = totalSalidas;
            
            const flujoNeto = totalEntradas + totalSalidas;
            updates[`summary.${FLUJO_NETO_INDEX}.${month}`] = flujoNeto;
            
            const saldoInicial = parseFloat(summary[SALDO_INICIAL_INDEX][month]) || 0;
            const saldoFinal = saldoInicial + flujoNeto;
            updates[`summary.${SALDO_FINAL_INDEX}.${month}`] = saldoFinal;
        });

        // Aplicar todas las actualizaciones en batch
        Object.entries(updates).forEach(([field, value]) => {
            change(field, value);
        });
    }, 0);
};

// Hook personalizado para manejar actualizaciones del resumen
export const useSummaryUpdater = (change, allValues) => {
    const updateSummary = React.useCallback(() => {
        updateSummaryTotalsOptimized(change, allValues);
    }, [change, allValues]);

    // Debounce para evitar múltiples llamadas innecesarias
    const debouncedUpdate = React.useCallback(
        debounce(updateSummary, 300),
        [updateSummary]
    );

    return debouncedUpdate;
};

// Función helper para debounce
export const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

// Función mejorada para manejar cambios en tiempo real
export const createSummaryUpdateHandler = (change, getFormValues) => {
    return debounce(() => {
        const currentValues = getFormValues();
        updateSummaryTotalsOptimized(change, currentValues);
    }, 100); // Debounce más corto para mejor responsividad
};

// Versión mejorada de la función original para compatibilidad
export const updateSummaryTotals = (allValues, change) => {
    // Usar la versión optimizada con mejor timing
    updateSummaryTotalsOptimized(change, allValues);
};

// Higher Order Component para manejo automático de actualizaciones del resumen
export const withSummaryUpdater = (WrappedComponent) => {
    return React.forwardRef((props, ref) => {
        const { allValues, change, ...otherProps } = props;
        
        // Crear handler optimizado
        const updateHandler = React.useMemo(() => {
            return createSummaryUpdateHandler(change, () => allValues);
        }, [change, allValues]);

        // Función mejorada que se pasa a los componentes hijos
        const handleValueChange = React.useCallback(() => {
            updateHandler();
        }, [updateHandler]);

        // Función para manejar eliminaciones
        const handleDeleteWithUpdate = React.useCallback((deleteFunction) => {
            return (...args) => {
                if (deleteFunction) {
                    deleteFunction(...args);
                }
                // Actualizar después de eliminar con un delay mayor
                setTimeout(() => updateHandler(), 200);
            };
        }, [updateHandler]);

        return (
            <WrappedComponent
                ref={ref}
                {...otherProps}
                allValues={allValues}
                change={change}
                onValueChange={handleValueChange}
                onDeleteWithUpdate={handleDeleteWithUpdate}
            />
        );
    });
};

// Componente de input optimizado que actualiza el resumen automáticamente
export const AutoUpdateInput = ({ onValueChange, ...props }) => {
    const handleBlur = React.useCallback((event) => {
        // Llamar el onBlur original si existe
        if (props.onBlur) {
            props.onBlur(event);
        }
        
        // Actualizar resumen con delay
        if (onValueChange) {
            setTimeout(onValueChange, 50);
        }
    }, [props.onBlur, onValueChange]);

    return (
        <Field
            {...props}
            onBlur={handleBlur}
        />
    );
};

// Función para calcular totales del resumen en tiempo real (sin actualizar Redux Form)
export const calculateSummaryTotalsLocal = (allValues) => {
    if (!allValues) {
        return createEmptySummaryTotals();
    }
    
    const { investments, operation, financial } = allValues;
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

    // Inicializar totales
    const summaryTotals = {
        entradas: { total: 0 },
        salidas: { total: 0 },
        flujoNeto: { total: 0 },
        saldoFinal: { total: 0 }
    };

    // Inicializar todos los meses
    months.forEach(month => {
        summaryTotals.entradas[month] = 0;
        summaryTotals.salidas[month] = 0;
        summaryTotals.flujoNeto[month] = 0;
        summaryTotals.saldoFinal[month] = 0;
    });

    // Calcular entradas, salidas y flujo neto para cada mes
    months.forEach(month => {
        let totalEntradas = 0;
        let totalSalidas = 0;

        // Procesar flujos financieros y operativos
        const flujosNormales = [
            ...(financial || []),
            ...(operation || []).filter(item => !item.isTitulo),
        ];

        flujosNormales.forEach(item => {
            if (item.isTitulo || item.is_not_suma) {
                return;
            }

            const valor = parseFloat(item[month]) || 0;
            
            if (valor < 0) {
                totalSalidas += valor;
            } else {
                totalEntradas += valor;
            }
        });

        // Procesar inversiones (siempre negativas)
        const inversionesData = investments && investments.results ? investments.results : [];
        inversionesData.forEach(item => {
            if (item.isTitulo || item.is_not_suma) {
                return;
            }

            const valor = parseFloat(item[month]) || 0;
            const valorInversion = valor > 0 ? -valor : valor;
            totalSalidas += valorInversion;
        });

        // Guardar totales calculados
        summaryTotals.entradas[month] = totalEntradas;
        summaryTotals.salidas[month] = totalSalidas;
        summaryTotals.flujoNeto[month] = totalEntradas + totalSalidas;

        // Actualizar totales anuales
        summaryTotals.entradas.total += totalEntradas;
        summaryTotals.salidas.total += totalSalidas;
        summaryTotals.flujoNeto.total += summaryTotals.flujoNeto[month];
    });

    // Calcular saldo final de forma acumulativa
    let saldoAcumulado = 0;
    
    months.forEach((month, index) => {
        if (index === 0) {
            // Para enero: Saldo Inicial (del backend) + Flujo Neto de Enero
            const saldoInicial = parseFloat(
                allValues.summary && allValues.summary[0] && allValues.summary[0].enero 
                    ? allValues.summary[0].enero 
                    : 0
            ) || 0;
            saldoAcumulado = saldoInicial + summaryTotals.flujoNeto[month];
        } else {
            // Para febrero en adelante: Saldo Final del mes anterior + Flujo Neto del mes actual
            saldoAcumulado = saldoAcumulado + summaryTotals.flujoNeto[month];
        }
        
        summaryTotals.saldoFinal[month] = saldoAcumulado;
        summaryTotals.saldoFinal.total += saldoAcumulado;
    });

    return summaryTotals;
};

// Función para crear estructura vacía de totales
export const createEmptySummaryTotals = () => {
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    
    const empty = { total: 0 };
    months.forEach(month => empty[month] = 0);
    
    return {
        entradas: { ...empty },
        salidas: { ...empty },
        flujoNeto: { ...empty },
        saldoFinal: { ...empty }
    };
};

// Hook para usar totales calculados localmente
export const useSummaryTotalsLocal = (allValues) => {
    return React.useMemo(() => {
        return calculateSummaryTotalsLocal(allValues);
    }, [allValues]);
};

// Función optimizada para actualizar solo cuando sea necesario (al guardar)
export const updateSummaryTotalsOnSave = (change, localTotals) => {
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

    // Índices del resumen
    const ENTRADAS_INDEX = 1;
    const SALIDAS_INDEX = 2;
    const FLUJO_NETO_INDEX = 3;
    const SALDO_FINAL_INDEX = 4;

    // Actualizar todos los campos de una vez
    const updates = {};
    
    months.forEach(month => {
        updates[`summary.${ENTRADAS_INDEX}.${month}`] = localTotals.entradas[month];
        updates[`summary.${SALIDAS_INDEX}.${month}`] = localTotals.salidas[month];
        updates[`summary.${FLUJO_NETO_INDEX}.${month}`] = localTotals.flujoNeto[month];
        updates[`summary.${SALDO_FINAL_INDEX}.${month}`] = localTotals.saldoFinal[month];
    });

    // Aplicar todas las actualizaciones en batch
    Object.entries(updates).forEach(([field, value]) => {
        change(field, value);
    });
}; 